// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// main.ts — screens, input and the net glue. The rules, the search, the geometry and the palette
// all live elsewhere and are pure; this is the only file that touches the DOM or the wire.
//
// THE NETCODE IN ONE PARAGRAPH. brood is LOCKSTEP with no authority at all, because there is
// nothing to be authoritative about: no randomness, no hidden information, and a move list that
// fully determines the position. Every peer broadcasts THE ENTIRE PLY LOG on every move — a whole
// game is a few dozen small integers. A dropped message is therefore a non-event, a peer that
// joins late is instantly in sync from one message, and a peer running a different build is
// DETECTED rather than tolerated: its log is replayed through OUR generator, and if a ply does not
// apply we say so instead of diverging quietly.

import '@ben-gy/game-engine/mobile.css';
import './styles/main.css';

import { hardenViewport } from '@ben-gy/game-engine/mobile';
import { createSfx } from '@ben-gy/game-engine/sound';
import { createStore } from '@ben-gy/game-engine/storage';
import { createNet, roomAppId, setTurnConfig, type Net } from '@ben-gy/game-engine/net';
import { getTurnConfig } from '@ben-gy/game-engine/turn';
import { createRounds, type RoundInfo, type Rounds } from '@ben-gy/game-engine/rematch';
import { clearRoomInUrl, createLobby, createRoomEntry, normalizeRoomCode, setRoomInUrl } from '@ben-gy/game-engine/lobby';
import { resolveName } from '@ben-gy/game-engine/identity';

import { DEFAULT_MODE, MODE_IDS, modeOf, type ModeId } from './modes';
import { KINDS, MATRIARCH, TENDER, tileKind, tileSeat } from './kinds';
import { Game } from './game';
import { SKILLS, SKILL_IDS, chooseMove, skillOf } from './search';
import { createView, meterHtml, type Marks, type View } from './render';
import { needsConfirm, snapTo } from './layout';
import { PALETTE, seatColour, seatName } from './palette';
import { runCountdown } from './countdown';
import {
  closedSides,
  liftable,
  matriarchForced,
  placementCells,
  shunts,
  type Move,
} from './rules';

const SLUG = 'brood';
const store = createStore(SLUG);
const sfx = createSfx({ muted: store.get('muted', false) as boolean });

const app = document.querySelector<HTMLElement>('#app')!;
app.innerHTML = `
  <main class="main-content" id="content"></main>
  <footer class="site-footer">
    Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a> ·
    <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more games, tools &amp; sites</a>
  </footer>`;
const content = app.querySelector<HTMLElement>('#content')!;

// ---------------------------------------------------------------- app state

let modeChoice: ModeId = modeOf(store.get('mode', DEFAULT_MODE)).id;
let skillChoice = skillOf(store.get('skill', 'forager')).id;
let hostMode: ModeId = DEFAULT_MODE;
// A handle that differs per DEVICE — two players both called "Brood 1" is a lobby you cannot read.
// resolveName also consumes any `?n=` on the way in, and must run at BOOT, before anything builds
// an invite link, or that param would ride along and rename whoever accepts it.
const playerName = resolveName(store, () => `Brood ${100 + Math.floor(Math.random() * 900)}`);

let game: Game | null = null;
let view: View | null = null;
let selfSeat = 0;
let botTimer: ReturnType<typeof setTimeout> | null = null;
let botThinking = false;
let mismatch = false;
let lastLanded: number | null = null;

/** What the finger is currently in the middle of doing. */
type Phase =
  | { k: 'idle' }
  | { k: 'armed'; kind: number }
  | { k: 'selected'; from: number }
  | { k: 'lift-pick'; tender: number }
  | { k: 'lift-drop'; tender: number; from: number };
let phase: Phase = { k: 'idle' };
let aimed: number | null = null;
let notice = '';

let net: Net | null = null;
let rounds: Rounds<{ mode: string }> | null = null;
let roomCode = '';
let seats: Array<{ id: string; name: string }> = [];
let sendLog: ((m: { p: number[] }) => void) | null = null;
let logOff: (() => void) | null = null;
let deepLinkUsed = false;
let lobby: { destroy(): void; repaint(): void } | null = null;
let tally: [number, number] = [0, 0];
let walkTimer: ReturnType<typeof setInterval> | null = null;
let walkLeft = 0;

const cleanups: Array<() => void> = [];
const onCleanup = (fn: () => void): void => void cleanups.push(fn);

function teardown(): void {
  while (cleanups.length) {
    try {
      cleanups.pop()!();
    } catch {
      /* a failed teardown must never block the next screen */
    }
  }
  if (botTimer) clearTimeout(botTimer);
  botTimer = null;
  botThinking = false;
  stopWalkover();
  view?.destroy();
  view = null;
}

const setPlaying = (on: boolean): void => void document.body.classList.toggle('playing', on);
const esc = (s: string): string =>
  s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);

const nameOf = (seat: number): string =>
  seats[seat]?.name ?? (seats.length === 0 ? (seat === selfSeat ? 'You' : SKILLS[skillChoice].name) : `Player ${seat + 1}`);

// ---------------------------------------------------------------- menu

function showMenu(): void {
  teardown();
  setPlaying(false);
  const m = modeOf(modeChoice);
  content.innerHTML = `
    <section class="screen menu">
      <h1 class="title">brood</h1>
      <p class="tagline">There is no board — only the cluster you and your rival build, until one Matriarch has all six sides closed.</p>

      <div class="chips" id="modes" role="group" aria-label="Rules">
        ${MODE_IDS.map(
          (id) =>
            `<button class="chip${id === modeChoice ? ' on' : ''}" data-mode="${id}" aria-pressed="${id === modeChoice}">${esc(modeOf(id).name)}</button>`,
        ).join('')}
      </div>
      <p class="blurb">${esc(m.blurb)}</p>

      <div class="chips" id="skills" role="group" aria-label="Opponent">
        ${SKILL_IDS.map(
          (id) =>
            `<button class="chip small${id === skillChoice ? ' on' : ''}" data-skill="${id}" aria-pressed="${id === skillChoice}">${esc(SKILLS[id].name)}</button>`,
        ).join('')}
      </div>
      <p class="blurb">${esc(SKILLS[skillChoice].blurb)}</p>

      <button class="btn primary" id="solo">Play solo</button>
      <button class="btn" id="friends">Play with a friend</button>
      <div class="row">
        <button class="btn link" id="howto">How to play</button>
        <button class="btn link" id="about">About</button>
        <button class="btn link" id="mute">${sfx.muted() ? 'Sound off' : 'Sound on'}</button>
      </div>
    </section>`;

  content.querySelectorAll<HTMLElement>('#modes .chip').forEach((b) =>
    b.addEventListener('click', () => {
      modeChoice = modeOf(b.dataset.mode).id;
      store.set('mode', modeChoice);
      sfx.play('blip');
      showMenu();
    }),
  );
  content.querySelectorAll<HTMLElement>('#skills .chip').forEach((b) =>
    b.addEventListener('click', () => {
      skillChoice = skillOf(b.dataset.skill).id;
      store.set('skill', skillChoice);
      sfx.play('blip');
      showMenu();
    }),
  );
  content.querySelector('#solo')!.addEventListener('click', () => {
    sfx.unlock();
    startSolo();
  });
  content.querySelector('#friends')!.addEventListener('click', () => {
    sfx.unlock();
    showRoomEntry();
  });
  content.querySelector('#howto')!.addEventListener('click', () => showModal('howto'));
  content.querySelector('#about')!.addEventListener('click', () => showModal('about'));
  content.querySelector('#mute')!.addEventListener('click', () => {
    sfx.setMuted(!sfx.muted());
    store.set('muted', sfx.muted());
    showMenu();
  });

  if (!store.get('seen-howto', false)) {
    store.set('seen-howto', true);
    showModal('howto');
  }
}

// ---------------------------------------------------------------- modals

const HOWTO = (): string => `
  <p><strong>Box in the other Matriarch.</strong> When every side of her is touching a tile — yours <em>or</em> her own — that side loses.</p>
  <p>On your turn you either <strong>put a new tile down</strong> from your supply, or <strong>move one</strong> already on the table. A new tile must touch your own and none of theirs. Your Matriarch must be down by your fourth tile, and nothing of yours moves until she is.</p>
  <p>The tiles are the board, so the tiles must stay in <strong>one connected group</strong> — a tile holding the group together simply cannot move, and is drawn with stripes so you can see it. A walking tile also has to physically fit through the gap it slides across.</p>
  <ul class="laws">
    ${KINDS.map((k) => `<li><span class="lg">${k.glyph}</span><span><strong>${esc(k.name)}</strong> — ${esc(k.law)}</span></li>`).join('')}
  </ul>
  <p class="perfect">Tap a tile to pick it up; the spaces it can reach light up. Tap it again to put it down.</p>`;

const ABOUT = `
  <p><strong>brood</strong> is a two-player abstract with no board: the cluster of tiles you both build is the board, and it grows in whatever direction you push it. Everything you see is drawn from the rules — there are no images and no fonts to download, and it works offline.</p>
  <p>Playing with a friend is <strong>peer-to-peer</strong>: your moves go straight between browsers over WebRTC. There is no game server and nothing about your game is stored anywhere. A public signalling relay is used only to introduce the two browsers to each other, and connecting exchanges IP addresses with the person you are playing, as any direct connection does.</p>
  <p>Page views are counted anonymously with Cloudflare Web Analytics. No cookies, no tracking, no accounts.</p>`;

function showModal(which: 'howto' | 'about'): void {
  const wrap = document.createElement('div');
  wrap.className = 'modal-back';
  wrap.innerHTML = `
    <div class="modal" role="dialog" aria-modal="true" aria-label="${which === 'howto' ? 'How to play' : 'About'}">
      <h2>${which === 'howto' ? 'How to play' : 'About brood'}</h2>
      ${which === 'howto' ? HOWTO() : ABOUT}
      <button class="btn" data-close>Got it</button>
    </div>`;
  document.body.appendChild(wrap);

  const close = (): void => {
    wrap.remove();
    document.removeEventListener('keydown', onKey);
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') close();
  };
  wrap.querySelector('[data-close]')!.addEventListener('click', close);
  // A tap anywhere off the panel closes it. pointerdown covers mouse, touch and pen in one
  // handler, and the guard is what stops the panel's own taps from closing it.
  wrap.addEventListener('pointerdown', (e) => {
    if (!(e.target as HTMLElement).closest('.modal')) close();
  });
  document.addEventListener('keydown', onKey);
}

// ---------------------------------------------------------------- a round

function startSolo(): void {
  teardown();
  seats = [];
  selfSeat = 0;
  mismatch = false;
  tally = [0, 0];
  resetPhase();
  game = new Game(modeChoice);
  showGame();
  runCountdown(content.querySelector<HTMLElement>('.stage') ?? content, sfx, () => {
    paint();
    maybeBot();
  });
}

function resetPhase(): void {
  phase = { k: 'idle' };
  aimed = null;
  notice = '';
  lastLanded = null;
}

function showGame(): void {
  setPlaying(true);
  content.innerHTML = `
    <section class="screen game">
      <header class="hud" id="hud"></header>
      <div class="stage" id="stage"><div id="board"></div></div>
      <div class="rail">
        <p class="prompt" id="prompt"></p>
        <div class="tray" id="tray"></div>
        <div class="controls" id="controls"></div>
      </div>
    </section>`;

  const boardEl = content.querySelector<HTMLElement>('#board')!;
  view = createView(boardEl);

  // Resize is driven by real events AND a retry, because a tab that is not laid out yet measures 0
  // and a board sized from 0 is a blank page.
  const doResize = (): void => {
    if (!view?.resize()) setTimeout(doResize, 120);
    else paint();
  };
  window.addEventListener('resize', doResize);
  window.addEventListener('orientationchange', doResize);
  const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => doResize()) : null;
  ro?.observe(content.querySelector<HTMLElement>('#stage')!);
  onCleanup(() => {
    window.removeEventListener('resize', doResize);
    window.removeEventListener('orientationchange', doResize);
    ro?.disconnect();
  });

  const stage = content.querySelector<HTMLElement>('#stage')!;
  stage.addEventListener('pointerdown', onStagePointer);
  onCleanup(() => stage.removeEventListener('pointerdown', onStagePointer));

  requestAnimationFrame(doResize);
  setTimeout(doResize, 60);
  doResize();
}

const myTurn = (): boolean => {
  const g = game;
  if (!g || g.over || mismatch) return false;
  if (selfSeat < 0) return false; // a spectator watches
  return g.turn === selfSeat;
};

// ---------------------------------------------------------------- what can be tapped

/** The cells the current phase offers, deduplicated. Deduplication matters: a Tender's own step
 *  destination is ALWAYS also one of her lift landings, and a Climber's destination can be one of
 *  your other movable tiles, so the same hex genuinely carries two meanings. */
function candidates(): number[] {
  const g = game;
  if (!g || !myTurn()) return [];
  switch (phase.k) {
    case 'idle':
      return [];
    case 'armed':
      return placementCells(g.pos);
    case 'selected': {
      const from = phase.from;
      return [...new Set(g.legal().filter((m) => m.t === 'move' && m.from === from).map((m) => (m as { to: number }).to))];
    }
    case 'lift-pick':
      return [...new Set(shunts(g.pos, phase.tender).map((s) => s.from))];
    case 'lift-drop': {
      const t = phase.tender;
      const f = phase.from;
      return [...new Set(shunts(g.pos, t).filter((s) => s.from === f).map((s) => s.to))];
    }
  }
}

/** Every cell whose top tile could be picked up right now. */
function movableCells(): Set<number> {
  const g = game;
  const out = new Set<number>();
  if (!g || !myTurn()) return out;
  for (const m of g.legal()) {
    if (m.t === 'move') out.add(m.from);
    else if (m.t === 'shunt') out.add(m.tender);
  }
  return out;
}

/** Every tile on the table that cannot move at all — pinned by the cluster, held after a lift, or
 *  buried under a Climber. Drawn with a hatch so the rule teaches itself. */
function pinnedCells(): number[] {
  const g = game;
  if (!g) return [];
  const out: number[] = [];
  for (const k of g.pos.occupiedCells()) {
    if (!liftable(g.pos, k) || g.pos.stunned(k)) out.push(k);
  }
  return out;
}

function focusSet(): number[] | null {
  const g = game;
  if (!g) return null;
  const cs = candidates();
  if (cs.length === 0) return null;
  const anchor =
    phase.k === 'selected' ? [phase.from] : phase.k === 'lift-pick' || phase.k === 'lift-drop' ? [phase.tender] : [];
  // Both Matriarchs stay in the action frame: this is a game about six specific cells, and losing
  // sight of them mid-decision is losing sight of the game.
  const queens = [g.pos.matriarch[0], g.pos.matriarch[1]].filter((k) => k >= 0);
  return [...new Set([...cs, ...anchor, ...queens])];
}

// ---------------------------------------------------------------- input

function onStagePointer(e: PointerEvent): void {
  const g = game;
  const v = view;
  if (!g || !v || !myTurn()) return;
  // clientX minus getBoundingClientRect — never offsetX/offsetY, and never elementFromPoint, which
  // returns null in a background tab and drops every tap with a clean console.
  const hex = v.hexAtClient(e.clientX, e.clientY);
  const { x, y } = v.toStage(e.clientX, e.clientY);
  const cands = candidates();

  // 1. The finger is on a candidate cell.
  if (cands.includes(hex)) return void aimOrCommit(hex, cands);

  // 2. The finger is on a real tile. That ALWAYS means the tile, never a destination beside it —
  //    resolving a tap on a pinned tile to a neighbouring destination would commit a move of a
  //    completely different piece, irreversibly, over the wire.
  if (g.pos.occupied(hex)) return void tapTile(hex);

  // 3. An empty cell that is not a candidate: forgive a near miss, otherwise put the piece down.
  const near = snapTo(v.frame(), cands, x, y);
  if (near !== null) return void aimOrCommit(near, cands);
  if (phase.k !== 'idle') {
    resetPhase();
    sfx.play('blip', { gain: 0.4 });
    paint();
  }
}

function aimOrCommit(hex: number, cands: readonly number[]): void {
  const v = view;
  if (!v) return;
  // Under 44px apart, a tap AIMS and a full-width button commits. Over it, a tap commits. The
  // threshold is measured from where the candidates actually landed on screen, not guessed from
  // whether the frame is clamped — those are different questions.
  if (needsConfirm(v.frame(), cands)) {
    if (aimed !== hex) {
      aimed = hex;
      sfx.play('blip', { gain: 0.5 });
      paint();
      return;
    }
  }
  commitAt(hex);
}

function commitAt(hex: number): void {
  const g = game;
  if (!g) return;
  switch (phase.k) {
    case 'armed':
      return void commit({ t: 'place', kind: phase.kind, to: hex });
    case 'selected':
      return void commit({ t: 'move', from: phase.from, to: hex });
    case 'lift-pick':
      phase = { k: 'lift-drop', tender: phase.tender, from: hex };
      aimed = null;
      notice = 'Now tap where to set it down.';
      sfx.play('select');
      return void paint();
    case 'lift-drop':
      return void commit({ t: 'shunt', tender: phase.tender, from: phase.from, to: hex });
    default:
      return;
  }
}

function tapTile(hex: number): void {
  const g = game;
  if (!g) return;
  if (phase.k === 'selected' && phase.from === hex) {
    resetPhase();
    sfx.play('blip', { gain: 0.4 });
    return void paint();
  }
  if (movableCells().has(hex)) {
    phase = { k: 'selected', from: hex };
    aimed = null;
    notice = '';
    sfx.play('select');
    return void paint();
  }
  // Say WHY, rather than doing nothing. This is the rule players find hardest.
  const t = g.pos.top(hex);
  if (t < 0) return;
  if (tileSeat(t) !== g.turn) notice = `That is ${seatName(tileSeat(t))}’s tile.`;
  else if (g.pos.stunned(hex)) notice = 'Held still — a lift froze it for a moment.';
  else if (g.pos.matriarch[g.turn] < 0) notice = 'Nothing moves until your Matriarch is down.';
  else notice = 'That tile is holding the cluster together, so it cannot move.';
  sfx.play('blip', { gain: 0.3 });
  paint();
}

function commit(move: Move): void {
  const g = game;
  if (!g || !myTurn()) return;
  if (!g.play(move)) {
    notice = 'That is not a legal move.';
    return void paint();
  }
  lastLanded = move.t === 'pass' ? null : move.t === 'place' ? move.to : move.to;
  resetPhase();
  sfx.play(move.t === 'place' ? 'hit' : 'select');
  broadcast();
  paint();
  if (g.over) return void finish();
  maybeBot();
}

// ---------------------------------------------------------------- the bot

/** The bot moves off the critical path — a timeout, so the board has painted the player's move
 *  before the search starts and the UI never appears to hang mid-tap. */
function maybeBot(): void {
  const g = game;
  if (!g || g.over || seats.length > 0 || botThinking) return;
  if (g.turn === selfSeat) return;
  botThinking = true;
  paint();
  botTimer = setTimeout(() => {
    botTimer = null;
    if (!game || game.over) {
      botThinking = false;
      return;
    }
    const chosen = chooseMove(game.pos, SKILLS[skillChoice]);
    botThinking = false;
    if (chosen && game.play(chosen.move)) {
      lastLanded = chosen.move.t === 'pass' ? null : chosen.move.to;
      sfx.play(chosen.move.t === 'place' ? 'hit' : 'blip');
      paint();
      if (game.over) return void finish();
    }
    paint();
  }, 240);
}

// ---------------------------------------------------------------- painting

function paint(): void {
  const g = game;
  if (!g || !view) return;
  const cands = candidates();
  const marks: Marks = {
    place: phase.k === 'armed' ? cands : [],
    dest: phase.k === 'selected' ? cands : [],
    lift: phase.k === 'lift-pick' || phase.k === 'lift-drop' ? cands : [],
    selected: phase.k === 'selected' ? phase.from : phase.k === 'idle' || phase.k === 'armed' ? null : phase.tender,
    aimed,
    pinned: pinnedCells(),
    landed: lastLanded,
    self: selfSeat < 0 ? 0 : selfSeat,
  };
  view.paint(g.pos, marks, focusSet());
  paintHud();
  paintTray();
  paintControls();
}

function paintHud(): void {
  const g = game;
  const hud = content.querySelector<HTMLElement>('#hud');
  if (!g || !hud) return;
  hud.innerHTML = `
    <div class="sides">
      ${[0, 1]
        .map((seat) => {
          const closed = closedSides(g.pos, seat);
          const down = g.pos.matriarch[seat] >= 0;
          return `<div class="side${seat === g.turn && !g.over ? ' active' : ''}">
            <span class="pip" style="background:${seatColour(seat)}"></span>
            <span class="who">${esc(nameOf(seat))}${seat === selfSeat && seats.length > 0 ? ' (you)' : ''}</span>
            ${down ? meterHtml(closed, g.mode.surroundAt, seat) : '<span class="rstat">—</span>'}
          </div>`;
        })
        .join('')}
    </div>
    <button class="btn tiny" id="quit">${seats.length ? 'Leave' : 'Menu'}</button>`;
  hud.querySelector('#quit')!.addEventListener('click', () => (seats.length ? backToLobby() : showMenu()));
}

function paintTray(): void {
  const g = game;
  const tray = content.querySelector<HTMLElement>('#tray');
  if (!g || !tray) return;
  if (!myTurn()) {
    tray.innerHTML = '';
    return;
  }
  const seat = g.turn;
  const forced = matriarchForced(g.pos, seat);
  tray.innerHTML = KINDS.map((k) => {
    const left = g.pos.hands[seat][k.kind];
    if (g.mode.supply[k.kind] === 0) return '';
    const off = left <= 0 || (forced && k.kind !== MATRIARCH);
    const on = phase.k === 'armed' && phase.kind === k.kind;
    return `<button class="suptile${on ? ' on' : ''}" data-kind="${k.kind}"${off ? ' disabled' : ''}
        aria-pressed="${on}" title="${esc(k.name)} — ${esc(k.law)}">
      <span class="sg" style="--seat:${seatColour(seat)}">${k.glyph}</span>
      <span class="sn">${esc(k.name)} ×${left}</span>
    </button>`;
  }).join('');
  tray.querySelectorAll<HTMLElement>('.suptile').forEach((b) =>
    b.addEventListener('click', () => {
      const kind = Number(b.dataset.kind);
      phase = phase.k === 'armed' && phase.kind === kind ? { k: 'idle' } : { k: 'armed', kind };
      aimed = null;
      notice = '';
      sfx.play('blip');
      paint();
    }),
  );
}

function paintControls(): void {
  const g = game;
  const el = content.querySelector<HTMLElement>('#controls');
  const prompt = content.querySelector<HTMLElement>('#prompt');
  if (!g || !el || !prompt) return;
  el.innerHTML = '';

  if (mismatch) {
    prompt.textContent = 'This browser is running a different build — the game cannot be kept in step.';
    return;
  }
  if (g.over) {
    prompt.textContent = '';
    return;
  }
  if (selfSeat < 0) {
    prompt.textContent = 'Watching — you are in the next round.';
    return;
  }
  if (!myTurn()) {
    prompt.textContent = botThinking ? 'Thinking…' : `${nameOf(g.turn)} to move.`;
    if (walkLeft > 0) prompt.textContent = `${nameOf(g.turn === selfSeat ? 1 - selfSeat : g.turn)} left the room — you win in ${Math.ceil(walkLeft / 1000)}s.`;
    return;
  }

  const cands = candidates();
  const confirming = aimed !== null && cands.includes(aimed);
  const only = g.legal();
  if (only.length === 1 && only[0].t === 'pass') {
    prompt.textContent = 'Nothing you own can move, and you have nothing left to put down.';
    el.innerHTML = `<button class="btn primary" id="pass">Pass</button>`;
    el.querySelector('#pass')!.addEventListener('click', () => commit({ t: 'pass' }));
    return;
  }

  if (confirming) {
    prompt.textContent = 'These spaces are close together — check the highlight, then confirm.';
    el.innerHTML = `<button class="btn primary" id="go">Put it here</button><button class="btn" id="nope">Pick another</button>`;
    el.querySelector('#go')!.addEventListener('click', () => commitAt(aimed!));
    el.querySelector('#nope')!.addEventListener('click', () => {
      aimed = null;
      paint();
    });
    return;
  }

  switch (phase.k) {
    case 'idle':
      prompt.textContent =
        notice || (matriarchForced(g.pos, g.turn) ? 'Your Matriarch must go down this turn.' : 'Tap a tile to move it, or pick one from your supply.');
      break;
    case 'armed':
      prompt.textContent = `${KINDS[phase.kind].name} — tap a marked space.`;
      el.innerHTML = `<button class="btn" id="cancel">Cancel</button>`;
      el.querySelector('#cancel')!.addEventListener('click', () => {
        resetPhase();
        paint();
      });
      break;
    case 'selected': {
      const from = phase.from;
      const kind = tileKind(g.pos.top(from));
      prompt.textContent = notice || `${KINDS[kind].name} — tap where it goes.`;
      const canLift = kind === TENDER && shunts(g.pos, from).length > 0;
      el.innerHTML =
        (canLift ? `<button class="btn" id="lift">Lift a neighbour instead</button>` : '') +
        `<button class="btn" id="cancel">Put it back</button>`;
      el.querySelector('#lift')?.addEventListener('click', () => {
        phase = { k: 'lift-pick', tender: from };
        aimed = null;
        notice = 'Tap the tile to lift over her.';
        sfx.play('blip');
        paint();
      });
      el.querySelector('#cancel')!.addEventListener('click', () => {
        resetPhase();
        paint();
      });
      break;
    }
    case 'lift-pick':
    case 'lift-drop':
      prompt.textContent = notice || 'Tap the tile to lift over her.';
      el.innerHTML = `<button class="btn" id="cancel">Cancel</button>`;
      el.querySelector('#cancel')!.addEventListener('click', () => {
        resetPhase();
        paint();
      });
      break;
  }
}

// ---------------------------------------------------------------- results

function finish(): void {
  const g = game;
  if (!g) return;
  stopWalkover();
  setPlaying(false);
  // CLOSE THE ROUND BEFORE ANYTHING TRIES TO DRAW A LOBBY. While the round's phase is still
  // 'playing' and this peer is seated, `lobby.ts` deliberately renders NOTHING — the game owns the
  // screen. So a results screen that embeds the lobby without ending the round first gets an empty
  // box where "play again" should be, which is a multiplayer session with no way to have another
  // one. It reopens voting locally on each peer, which is exactly what a rematch vote needs.
  rounds?.finish?.();
  const won = g.winner === selfSeat;
  sfx.play(g.winner < 0 ? 'blip' : won ? 'win' : 'lose');
  if (g.winner >= 0) tally[g.winner]++;

  const st = g.standings();
  const headline =
    g.winner < 0
      ? g.reason === 'both'
        ? 'Both Matriarchs closed at once — a draw'
        : g.reason === 'repetition'
          ? 'The same position three times — a draw'
          : g.reason === 'stalemate'
            ? 'Neither side can move — a draw'
            : 'Nobody could finish it — a draw'
      : g.reason === 'walkover'
        ? `${esc(nameOf(g.winner))} wins — the other player left`
        : won
          ? 'You closed her in'
          : `${esc(nameOf(g.winner))} closed her in`;

  content.innerHTML = `
    <section class="screen results">
      <h2>${headline}</h2>
      <table class="standings">
        <thead><tr><th>Player</th><th>Boxed in</th><th>Their best</th><th>Tiles</th><th>Moves</th></tr></thead>
        <tbody>
          ${st
            .map(
              (s) => `<tr class="${s.seat === g.winner ? 'won' : ''}">
                <td><span class="pip" style="background:${seatColour(s.seat)}"></span>${esc(nameOf(s.seat))}${tally[s.seat] ? ` · ${tally[s.seat]}★` : ''}</td>
                <td>${s.closed}/${g.mode.surroundAt}</td>
                <td>${s.bestPressure}/${g.mode.surroundAt}</td>
                <td>${s.placed}</td>
                <td>${s.moved}</td>
              </tr>`,
            )
            .join('')}
        </tbody>
      </table>
      <p class="perfect">${esc(perfectLine(g))}</p>
      <div class="row" id="after"></div>
      <button class="btn link results-feedback">Tell me what you think</button>
    </section>`;

  const after = content.querySelector<HTMLElement>('#after')!;
  if (seats.length > 0) {
    after.innerHTML = `<div id="lobby-slot" style="width:100%"></div>`;
    showLobby(content.querySelector<HTMLElement>('#lobby-slot')!);
  } else {
    after.innerHTML = `<button class="btn primary" id="again">Play again</button><button class="btn" id="menu">Menu</button>`;
    after.querySelector('#again')!.addEventListener('click', startSolo);
    after.querySelector('#menu')!.addEventListener('click', showMenu);
  }
  content.querySelector('.results-feedback')!.addEventListener('click', (e) =>
    window.feedback?.open({ returnFocusTo: e.currentTarget as HTMLElement }),
  );
}

/** The knowable "how close was that", so the summary is about the game rather than about a number:
 *  the closest each side ever came to finishing, and how many sides were still open at the end. */
function perfectLine(g: Game): string {
  const st = g.standings();
  const need = g.mode.surroundAt;
  if (g.winner < 0) {
    const best = Math.max(st[0].bestPressure, st[1].bestPressure);
    return `Nobody got past ${best} of ${need}. ${g.ply} plies.`;
  }
  const loser = 1 - g.winner;
  const missed = need - st[loser].bestPressure;
  return missed <= 0
    ? `${nameOf(loser)} had her at ${st[loser].bestPressure}/${need} too — it came down to who was quicker. ${g.ply} plies.`
    : `${nameOf(loser)} got to ${st[loser].bestPressure}/${need} — ${missed} side${missed === 1 ? '' : 's'} short. ${g.ply} plies.`;
}

// ---------------------------------------------------------------- multiplayer

function showRoomEntry(): void {
  teardown();
  setPlaying(false);
  const deep = deepLinkUsed ? null : new URL(location.href).searchParams.get('room');
  if (deep) {
    deepLinkUsed = true;
    const code = normalizeRoomCode(deep);
    clearRoomInUrl();
    if (code.length >= 3) return void enterRoom(code, false);
  }
  const entry = createRoomEntry({
    container: content,
    onSubmit: (code: string, created: boolean) => void enterRoom(normalizeRoomCode(code), created),
    onCancel: showMenu,
    title: 'Play with a friend',
    subtitle: 'Two players, one cluster. Start a room, or type a friend’s code.',
  });
  onCleanup(() => entry.destroy());
}

async function enterRoom(code: string, created: boolean): Promise<void> {
  teardown();
  setPlaying(false);
  roomCode = code;
  setRoomInUrl(code);
  game = null;
  seats = [];
  tally = [0, 0];
  content.innerHTML = `<section class="screen"><div class="lobby-searching"><span class="spinner"></span> Connecting…</div></section>`;

  try {
    setTurnConfig(await getTurnConfig());
  } catch {
    /* fail open — TURN is an upgrade, never a dependency */
  }

  const n = createNet(
    { appId: roomAppId(SLUG), roomId: code, claimHost: created },
    {
      onHostChange: () => repaintLobby(),
      onPeers: () => {
        // A room that has collapsed to one player mid-round is a WALKOVER, not a hang. It is armed
        // here as well as in onPeerLeave, because Trystero does not reliably report a tab that
        // simply vanished — it just stops being in the roster.
        //
        // `peers()` IS THE ROSTER, AND THE ROSTER INCLUDES YOU. An earlier cut compared it to 0,
        // which is unreachable, so the arm was dead — and worse, the `else` branch then fired on
        // every roster change and cancelled the timer that onPeerLeave had just armed on the same
        // synchronous stack. The survivor sat on a live board forever: exactly the hang this code
        // exists to prevent, written as if it prevented it.
        const alone = n.peers().length <= 1;
        if (alone && game && !game.over && seats.length === 2) armWalkover();
        else if (!alone) stopWalkover();
        repaintLobby();
      },
      onPeerLeave: () => {
        if (game && !game.over && seats.length === 2) armWalkover();
        repaintLobby();
      },
    },
  );
  net = n;

  const r = createRounds<{ mode: string }>({
    net: n,
    playerName,
    minPlayers: 2,
    roundOpts: () => ({ mode: modeChoice }),
    onRound: (info) => startNetRound(info),
    onChange: (s) => {
      const ho = s.hostOpts as { mode?: string } | null;
      if (ho && typeof ho.mode === 'string') hostMode = modeOf(ho.mode).id;
      repaintLobby();
    },
  });
  rounds = r;

  const send = n.channel<{ p: number[] }>('log', (data) => onRemoteLog(data));
  sendLog = (m) => send(m);
  // NOT onCleanup(). The cleanup list is PER SCREEN and teardown() runs on every screen change —
  // including startNetRound — so registering the channel there would detach the receiver at the
  // exact moment the round begins, and every move would be sent into a void. The channel belongs
  // to the ROOM, so it is torn down only in leaveRoom.
  logOff = () => send.off();

  showLobby();
}

function startNetRound(info: RoundInfo<{ mode: string }>): void {
  teardown();
  mismatch = false;
  resetPhase();
  const mode = modeOf(info.opts?.mode);
  seats = info.players.slice(0, 2).map((p) => ({ id: p.id, name: p.name }));
  selfSeat = seats.findIndex((s) => s.id === net?.selfId);
  if (!info.seated || selfSeat < 0) selfSeat = -1; // a peer that connected mid-round watches

  game = new Game(mode);
  showGame();
  runCountdown(content.querySelector<HTMLElement>('.stage') ?? content, sfx, () => paint());
}

function broadcast(): void {
  // The WHOLE log, every time. A few dozen small integers, and it makes a dropped message a
  // non-event and a late peer instantly in sync.
  if (game && sendLog) sendLog({ p: game.plies.slice() });
}

function onRemoteLog(data: { p: number[] }): void {
  const g = game;
  if (!g || !Array.isArray(data?.p)) return;
  const theirs = data.p.filter((x) => Number.isInteger(x) && x >= 0);
  if (theirs.length <= g.plies.length) return;
  // Replay their log through OUR generator. If it does not apply, they are running different code
  // — say so rather than diverging quietly.
  const rebuilt = Game.replay(g.mode, theirs);
  if (!rebuilt) {
    mismatch = true;
    view?.shake();
    paint();
    return;
  }
  game = rebuilt;
  lastLanded = null;
  resetPhase();
  sfx.play('blip', { gain: 0.6 });
  paint();
  if (rebuilt.over) return void finish();
}

/** Twelve seconds, visible, then the survivor wins. A waiting state with no horizon is a hang. */
function armWalkover(): void {
  if (walkTimer || !game || game.over || selfSeat < 0) return;
  walkLeft = 12_000;
  walkTimer = setInterval(() => {
    walkLeft -= 500;
    if (walkLeft > 0) return void paintControls();
    stopWalkover();
    if (game && !game.over && selfSeat >= 0) {
      game.walkover(selfSeat as 0 | 1);
      finish();
    }
  }, 500);
  paintControls();
}

function stopWalkover(): void {
  if (walkTimer) clearInterval(walkTimer);
  walkTimer = null;
  walkLeft = 0;
}

function repaintLobby(): void {
  lobby?.repaint();
}

function backToLobby(): void {
  teardown();
  setPlaying(false);
  // The engine's lobby paints nothing while a round is still "playing" and we are seated, so a
  // mid-round quit has to finish the round first or the screen is simply blank.
  rounds?.finish?.();
  game = null;
  showLobby();
}

function showLobby(host?: HTMLElement): void {
  const n = net;
  const r = rounds;
  if (!n || !r) return showMenu();
  setPlaying(false);
  if (!host) content.innerHTML = `<section class="screen"><div id="lobby-host"></div></section>`;
  const container = host ?? content.querySelector<HTMLElement>('#lobby-host')!;
  const isHost = (): boolean => n.isHost() && n.hostSettled();

  lobby = createLobby({
    container,
    net: n,
    rounds: r,
    roomCode,
    minPlayers: 2,
    maxPlayers: 2,
    onCancel: () => void leaveRoom(),
    modeSlot: () =>
      isHost()
        ? `<div class="lobby-modes" id="lm">${MODE_IDS.map(
            (id) =>
              `<button class="chip mode-btn${id === modeChoice ? ' on' : ''}" data-mode="${id}" aria-pressed="${id === modeChoice}">${esc(modeOf(id).name)}</button>`,
          ).join('')}<p class="mode-blurb">${esc(modeOf(modeChoice).blurb)}</p></div>`
        : `<p class="host-pick">${n.hostSettled() ? `The host chose <strong>${esc(modeOf(hostMode).name)}</strong>` : 'Waiting for the host…'}</p>`,
    onModeMount: () => {
      if (!isHost()) return;
      container.querySelectorAll<HTMLElement>('#lm .mode-btn').forEach((b) =>
        b.addEventListener('click', () => {
          modeChoice = modeOf(b.dataset.mode).id;
          store.set('mode', modeChoice);
          lobby?.repaint();
        }),
      );
    },
  });
  onCleanup(() => {
    lobby?.destroy();
    lobby = null;
  });
}

async function leaveRoom(): Promise<void> {
  teardown();
  clearRoomInUrl();
  const n = net;
  net = null;
  rounds = null;
  logOff?.();
  logOff = null;
  sendLog = null;
  seats = [];
  game = null;
  // Await the real teardown: Trystero defers it, and coming back to a room that is still dying is
  // the leave/rejoin trap that leaves both players alone in the right room code, forever.
  if (n) await n.leave();
  showMenu();
}

// ---------------------------------------------------------------- boot

hardenViewport();
window.addEventListener('beforeunload', () => void net?.leave());
showMenu();

// A synchronous hook so the browser pass can drive a whole game to its end without depending on
// rAF, which never fires in a background tab.
(window as unknown as { __brood: unknown }).__brood = {
  state: () => game,
  frame: () => view?.frame(),
  phase: () => phase,
  candidates,
  pinned: pinnedCells,
  palette: PALETTE,
  step: () => {
    if (!game || game.over) return false;
    const chosen = chooseMove(game.pos, { ...SKILLS.hatchling, timeMs: 0, nodes: 4000 });
    if (!chosen) return false;
    const ok = game.play(chosen.move);
    if (ok) {
      lastLanded = chosen.move.t === 'pass' ? null : chosen.move.to;
      broadcast();
      paint();
      if (game.over) finish();
    }
    return ok;
  },
  tapCell: (k: number) => {
    if (!game || !view) return false;
    const cands = candidates();
    if (cands.includes(k)) {
      aimOrCommit(k, cands);
      return true;
    }
    if (game.pos.occupied(k)) {
      tapTile(k);
      return true;
    }
    return false;
  },
  arm: (kind: number) => {
    phase = { k: 'armed', kind };
    aimed = null;
    paint();
  },
  plies: () => game?.plies.slice() ?? [],
};
